import { DateUtil } from "../utils/DateUtil.js";
import { Geom } from "../utils/Geom.js";
import { IData, VOAsset, VOBlog, VOBlogKind, VOConfig, VOExpeditionPhase, VOMedia, VOSupplyUnitKind, VORoutePhase } from "./DataStructure.js";
import { Language } from "./Language.js";
import { Sensor, SensorEntry, SensorGetMode } from "./Sensor.js";
import { AppStorage } from "./AppStorage.js";

export class DataManager {
    public onComplete = e5.Signal.create();
    public onProgress = e5.Signal.create<[number]>();

    public data: IData = null;

    public config: VOConfig;

    public sensorMosaic: Sensor;
    public sensorNansen: Sensor;

    public minDateMosaic: Date = new Date();
    public maxDateMosaic: Date = new Date();
    public minDateNansen: Date = new Date();
    public maxDateNansen: Date = new Date();

    public startDateNansen = DateUtil.fromString("1893-07-21");

    public filterBlogs: boolean = true;
    public warnSensorEntryNotFound: boolean = true;

    private _url: string = "";

    constructor() {

    }

    public load(url: string): void {
        var xhr = new XMLHttpRequest();
        this._url = url;
        $.ajax({
            xhr: () => {

                xhr.addEventListener("progress", (evt) => {

                    var loaded = evt.loaded;
                    var total = evt.total;

                    if (!evt.lengthComputable) {
                        total = parseInt(xhr.getResponseHeader("x-decompressed-content-length"));
                    }

                    if (total === 0 || isNaN(total))
                        return;

                    this.onProgress.dispatch(loaded / total);
                    // console.log(loaded, "of", total, Math.floor((loaded / total) * 100) + "%");
                }, false);
                return xhr;
            },
            type: 'GET',
            url: url,
            cache: false,
            timeout: 20000,
            error: (xhr) => {
                this.handleError();
            },
            success: (data) => {

                if (!data.config) {
                    this.handleError();
                    return;
                }

                //No more local storage saving the data,
                //to prevent "QuotaExceededError".
                AppStorage.removeItem("data");

                //No data saving in local storage for safari.
                // if (e5.Caps.isSafari) {
                //     AppStorage.removeItem("data");
                // }
                // else if (!AppStorage.setItem("data", xhr.responseText)) {
                //     AppStorage.removeItem("data");
                // }

                this.data = data;
                this.finalize();
                this.onComplete.dispatch();
            }
        });
    }

    private handleError(): void {
        if (AppStorage.getItem("data")) {
            console.warn("Use fallback data package.");
            this.data = JSON.parse(AppStorage.getItem("data"));
            this.finalize();
            this.onComplete.dispatch();
        }
        else {
            var msg = Language.current === "de" ? "Leider konnten die Daten nicht geladen werden" : "The data could not be loaded";
            e5.Toast.showText(msg, 120);
        }
    }

    private retry(): void {
        this.load(this._url);
    }

    private prepareMedia(media: VOMedia): void {
        if (!media)
            return;
        media.title_de = media.title_de || "";
        media.title_en = media.title_en || "";
        media.description_de = media.description_de || "";
        media.description_en = media.description_en || "";
        media.author_de = media.author_de || "";
        media.author_en = media.author_en || "";
    }

    public fromData(data: IData): void {
        this.data = data;
        this.finalize();
    }

    private finalize(): void {
        Language.data = this.data;

        this.config = e5.array.match(this.data.config, "title", "main");

        //TODO: fallback config
        if (!this.config) {
            console.warn("Missing config!");
        }

        for (var config of this.data.config) {
            if (config.start) {
                config.start = DateUtil.fromString(config.start + "");
            }
            else {
                config.start = DateUtil.fromString("2019-01-01")
            }

            if (config.end) {
                config.end = DateUtil.fromDateTimeString(config.end + "");
            }
            else {
                config.end = DateUtil.fromString("2020-10-13")
            }

            if (!config.phase) {
                config.phase = [];
            }

            for (var phase of config.phase) {
                phase.config_phase_start_date = DateUtil.fromString(phase.config_phase_start_date + "");
            }

            //Order DESC - From old to new
            config.phase.sort((a, b) => {
                return b.config_phase_start_date.getTime() - a.config_phase_start_date.getTime();
            });

            // if (config.minimum) {
            //     config.minimum = DateUtil.fromString(config.minimum + "");
            // }
            if (config.now) {
                config.now = DateUtil.fromString(config.now + "");
            } else {
                config.now = new Date();
            }

            if (!config.supply_units) {
                config.supply_units = [];
            }

            if (!config.time_of_release) {
                config.time_of_release = 15;
            }

            for (var supplyUnit of config.supply_units) {
                supplyUnit.config_supply_unit_start = DateUtil.fromString(supplyUnit.config_supply_unit_start + "");
                if (supplyUnit.config_supply_unit_end) {
                    supplyUnit.config_supply_unit_end = DateUtil.fromString(supplyUnit.config_supply_unit_end + "");
                }
                else {
                    supplyUnit.config_supply_unit_end = DateUtil.fromString("2050-01-01");
                }
            }

            // config.phase = [
            //     { date: config.start, phase: "start" },
            //     { date: config.search, phase: "search" },
            //     { date: config.found, phase: "found" },
            //     { date: config.camp, phase: "camp" },
            //     { date: config.departure, phase: "departure" },
            //     { date: config.departure_open_water, phase: "departure-open-water" },
            //     { date: config.end, phase: "end" },
            // ];
        }

        //Resolve dates
        for (var blog of this.data.blog) {
            if (!blog.links) {
                blog.links = [];
            }
            var d = DateUtil.fromString(blog.date + "");
            if (blog.kind === "nansen") {
                //With ACF (Advanced Custom Fields) you cannot set dates before 1900.
                d.setUTCFullYear(d.getUTCFullYear() - 100);
            }
            blog.date = DateUtil.round(d);
            blog.dateNormalized = blog.date;
            if (blog.kind === "nansen") {
                blog.dateNormalized = DateUtil.nansenToMosaic(blog.date);
            }
        }

        this.data.blog.sort((a, b) => {
            return b.date.getTime() - a.date.getTime();
        });

        // for (var config of this.data.config) {
        //     if (!config.minimum) {
        //         config.minimum = this.getMinMax(this.data.blog, "mosaic").min;
        //     }
        // }

        this.minDateMosaic = DateUtil.round(this.config.start);
        this.maxDateMosaic = DateUtil.round(this.config.end);
        this.minDateNansen = DateUtil.mosaicToNansen(this.minDateMosaic);
        this.maxDateNansen = DateUtil.mosaicToNansen(this.maxDateMosaic);

        for (var blog of this.data.blog) {
            this.prepareMedia(blog.image);
            this.prepareMedia(blog.video_de);
            this.prepareMedia(blog.video_en);

            blog.hasMedia = Boolean(
                (blog.media_type === "video" && blog.video_de || blog.video_en) ||
                (blog.media_type === "image" && blog.image) ||
                (blog.media_type === "youtube" && blog.youtube_de || blog.youtube_en));

            blog.captionDe = "";
            blog.captionEn = "";
            if (blog.hasMedia) {
                var titleDe = "";
                var titleEn = "";
                var descriptionDe = "";
                var descriptionEn = "";
                var authorDe = "";
                var authorEn = "";

                if (blog.media_type === "youtube") {
                    titleDe = blog.youtube_title_de || blog.youtube_title_en;
                    titleEn = blog.youtube_title_en || blog.youtube_title_de;
                    descriptionDe = blog.youtube_description_de || blog.youtube_description_en;
                    descriptionEn = blog.youtube_description_en || blog.youtube_description_de;
                    authorDe = blog.youtube_author_de || blog.youtube_author_en;
                    authorEn = blog.youtube_author_en || blog.youtube_author_de;
                }
                else if (blog.media_type === "image") {
                    titleDe = blog.image.title_de || blog.image.title_en;
                    titleEn = blog.image.title_en || blog.image.title_de;
                    descriptionDe = blog.image.description_de || blog.image.description_en;
                    descriptionEn = blog.image.description_en || blog.image.description_de;
                    authorDe = blog.image.author_de || blog.image.author_en;
                    authorEn = blog.image.author_en || blog.image.author_de;
                }
                else if (blog.media_type === "video") {
                    var videoDe = blog.video_de || blog.video_en;
                    var videoEn = blog.video_en || blog.video_de;
                    titleDe = videoDe.title_de || videoDe.title_en;
                    titleEn = videoEn.title_en || videoEn.title_de;
                    descriptionDe = videoDe.description_de || videoDe.description_en;
                    descriptionEn = videoEn.description_en || videoEn.description_de;
                    authorDe = videoDe.author_de || videoDe.author_en;
                    authorEn = videoEn.author_en || videoEn.author_de;
                }

                var de: string[] = [];
                if (titleDe)
                    de.push(titleDe);
                if (descriptionDe)
                    de.push(descriptionDe);
                if (authorDe)
                    de.push(authorDe);

                var en: string[] = [];
                if (titleEn)
                    en.push(titleEn);
                if (descriptionDe)
                    en.push(descriptionEn);
                if (authorDe)
                    en.push(authorEn);

                blog.captionDe = de.join(" ").trim();
                blog.captionEn = en.join(" ").trim();
            }
        }

        for (var graph of this.data.ice_extent_graphs) {
            for (var value of graph.values) {
                value.date = DateUtil.fromString(value.date as any);
            }
        }

        for (var blog of this.data.blog) {
            blog.day = this.dateToDay(blog.date, blog.kind);
        }

        this.sensorMosaic = new Sensor(this.data.sensor_data_mosaic);
        this.sensorNansen = new Sensor(this.data.sensor_data_nansen);

        for (var iceExtent of this.data.ice_extent) {
            iceExtent.date_string = String(iceExtent.date);
            iceExtent.date = new Date(iceExtent.date);
        }

        for (var blog of this.data.blog) {
            var latLng = this.getLatLng(blog.dateNormalized, blog.kind);
            blog.latitude = latLng.lat;
            blog.longitude = latLng.lng;
            blog.geoDms = Geom.ddToDms(blog.latitude, blog.longitude);
        }

        //Filter blogs up to today
        if (this.filterBlogs) {
            var filteredBlogs: VOBlog[] = [];
            var now = new Date();
            //After 15.00 UTC (or 17.00 GMT+2)
            var isAfterHours = now.getUTCHours() >= this.config.time_of_release;
            for (var blog of this.data.blog) {
                if (blog.kind === "mosaic") {
                    var c = DateUtil.compare(blog.date, this.maxDateMosaic);
                    if (c !== 1) {
                        if (c === 0 && !isAfterHours) {
                        }
                        else {
                            filteredBlogs.push(blog);
                        }
                    }
                }
                else if (blog.kind === "nansen") {
                    var c = DateUtil.compare(blog.date, this.maxDateNansen);
                    if (c !== 1) {
                        if (c === 0 && !isAfterHours) {
                        }
                        else {
                            filteredBlogs.push(blog);
                        }
                    }
                }
            }
            this.data.blog = filteredBlogs;
        }
    }

    public dateToDay(date: Date, kind: VOBlogKind): number {
        var day = DateUtil.dayDiff(date, kind === "nansen" ? this.startDateNansen : this.config.start);
        return day < 0 ? day : day + 1;
    }

    public getSupplyUnit(date: Date): VOSupplyUnitKind | "none" {
        for (var supplyUnit of this.config.supply_units) {
            if (DateUtil.isBtween(date, supplyUnit.config_supply_unit_start, supplyUnit.config_supply_unit_end)) {
                return supplyUnit.config_supply_unit_kind;
            }
        }
        return "none";
    }

    public getExpeditionPhase(date: Date): VOExpeditionPhase {
        var time = DateUtil.round(date).getTime();
        for (var phase of this.config.phase) {
            if (phase.config_phase_start_date && phase.config_phase_start_date.getTime() <= time) {
                return phase.config_phase_kind;
            }
        }
        return null;//"start";
    }

    public getRoutePhase(date: Date): VORoutePhase {
        var time = DateUtil.round(date).getTime();
        for (var phase of this.config.route_phase) {
            if (phase.config_route_phase_start_date && phase.config_route_phase_start_date.getTime() <= time) {
                return phase.config_route_phase_kind;
            }
        }
        return null;//"drift";
    }

    private getMinMax(blogs: VOBlog[], kind: VOBlogKind): { min: Date; max: Date } {
        var min = Number.MAX_VALUE;
        var max = -Number.MAX_VALUE;

        if (blogs.length === 0)
            return { min: new Date(), max: new Date() };

        for (var blog of blogs) {
            if (blog.kind != kind)
                continue;
            min = Math.min(blog.date.getTime(), min);
            max = Math.max(blog.date.getTime(), max);
        }
        return {
            min: this.data.blog.length > 0 ? new Date(min) : new Date(),
            max: this.data.blog.length > 0 ? new Date(max) : new Date()
        };
    }

    public getMilestone(date: Date, kind: VOBlogKind): VOBlog {
        date = DateUtil.round(date);
        var blogs: VOBlog[] = this.data.blog;
        for (var blog of blogs) {
            if (blog.kind === kind && blog.milestone && DateUtil.equal(blog.date, date)) {
                return blog;
            }
        }
        return null;
    }

    public getIceExtent(date: Date): number {
        var d = DateUtil.toString(date);
        for (var iceExtent of this.data.ice_extent) {
            if (iceExtent.date_string == d)
                return iceExtent.value;
        }

        var res: number = 0;
        for (var iceExtent of this.data.ice_extent) {
            if (DateUtil.smallerEqual(iceExtent.date, date)) {
                res = iceExtent.value;
            } else {
                break;
            }
        }
        return res || this.data.ice_extent[this.data.ice_extent.length - 1].value;
    }

    public getDistance(date: Date, mode: SensorGetMode = "nearest"): number {
        var entry = this.getSensorEntry(date, "mosaic", mode);
        return entry.distance;
    }

    public getWind(date: Date, mode: SensorGetMode = "nearest"): number {
        var entry = this.getSensorEntry(date, "mosaic", mode);
        return entry.wind;
    }

    public getWaterTemperature(date: Date, kind: VOBlogKind, mode: SensorGetMode = "nearest"): number {
        var entry = this.getSensorEntry(date, kind, mode);
        return entry.water_temperature;
    }

    public getTemperature(date: Date, kind: VOBlogKind, mode: SensorGetMode = "nearest"): number {
        var entry = this.getSensorEntry(date, kind, mode);
        return entry.temperature;
    }

    public getLatLng(date: Date, kind: VOBlogKind, mode: SensorGetMode = "nearest"): { lat: number; lng: number } {
        var entry = this.getSensorEntry(date, kind, mode);
        return { lat: entry.lat, lng: entry.lng };
    }

    public getXY(date: Date, kind: VOBlogKind, mode: SensorGetMode = "nearest"): e5.IPoint {
        var entry = this.getSensorEntry(date, kind, mode);
        return { x: entry.x, y: entry.y };
    }

    public getSensorEntry(date: Date, kind: VOBlogKind, mode: SensorGetMode = "nearest"): SensorEntry {
        if (!date) {
            console.warn("No date given!");
            return this.createFallbackSensorEntry(date);
        }
        var sensor = this.getSensor(kind);
        var entry = sensor.getEntry(date, mode);
        return entry ? entry : this.createFallbackSensorEntry(date);
    }

    private createFallbackSensorEntry(date: Date): SensorEntry {
        return {
            index: 0,
            date: date,
            dateKey: DateUtil.toKey(date),
            time: date.getTime(),
            lat: 0,
            lng: 0,
            x: 0,
            y: 0,
            temperature: 0,
            water_temperature: 0,
            wind: 0,
            distance: 0,
            phase: null,
            // pathData: "",
            // pathDataRest: ""
        };
    }

    public getSensor(kind: VOBlogKind): Sensor {
        return kind === "nansen" ? this.sensorNansen : this.sensorMosaic;
    }

    public getAsset(title: string): VOAsset {
        for (var asset of this.data.asset) {
            if (asset.title == title)
                return asset;
        }
        return null;
    }

    public getAssetUrl(title: string, fallback: string = ""): string {
        var asset = this.getAsset(title);
        return asset && asset.media ? asset.media.url : fallback;
    }

    public getIceExtentUrl(date: Date): string {
        var assetTitle = `map-ice-${date.getFullYear()}${e5.Text.fillHead((date.getMonth() + 1) + "", 2, "0")}`;
        return this.getAssetUrl(assetTitle);
    }
}